import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import BrandLogo from '../components/shared/BrandLogo';
import { BRAND_HERO_GRADIENT } from '../components/shared/SectionTitle';

const DataDeletionPage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-dark-950">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="md" />
          </Link>
          <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">Home</Link>
            <ChevronRightIcon className="w-3 h-3" />
            <Link to="/privacy" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacy Policy</Link>
            <ChevronRightIcon className="w-3 h-3" />
            <span className="text-gray-900 dark:text-white font-medium">Data Deletion</span>
          </nav>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="h-1.5 w-24 rounded-full mb-5" style={{ background: BRAND_HERO_GRADIENT }} aria-hidden />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">User Data Deletion</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-10">
          <strong>Last Updated:</strong> February 25, 2026
        </p>

        <div className="space-y-10 text-gray-700 dark:text-gray-300 leading-relaxed">
          <p>
            OmniConnX is committed to your privacy and gives you full control over your data.
            If you want to delete data that OmniConnX has collected through your use of our platform
            or via connected social media accounts (Facebook, Instagram, YouTube, TikTok), you can do so
            using any of the methods described below.
          </p>

          {/* Method 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              1. Disconnect a Platform (Remove Platform Data)
            </h2>
            <p className="mb-4">
              To remove all data associated with a specific connected platform (e.g., Facebook, Instagram):
            </p>
            <ol className="list-decimal list-outside ml-5 space-y-3">
              <li>Log in to your OmniConnX account at <span className="font-medium text-violet-600 dark:text-violet-400">omniconnx.com</span></li>
              <li>Navigate to <strong>Settings</strong> (click your profile icon in the sidebar)</li>
              <li>Go to the <strong>Platform Integrations</strong> section</li>
              <li>Find the platform you want to disconnect (e.g., Facebook, Instagram)</li>
              <li>Click the <strong>"Disconnect"</strong> button next to that platform</li>
              <li>Confirm the disconnection when prompted</li>
            </ol>
            <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>What happens:</strong> This immediately revokes and deletes your stored OAuth access tokens
                and refresh tokens from our systems. All future data syncing from that platform will stop.
                Historical analytics data that was previously synced will be retained unless you also request
                full data deletion (see Method 3 below).
              </p>
            </div>
          </section>

          {/* Method 2 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              2. Delete Your Entire Account
            </h2>
            <p className="mb-4">
              To permanently delete your OmniConnX account and all associated data:
            </p>
            <ol className="list-decimal list-outside ml-5 space-y-3">
              <li>Log in to your OmniConnX account</li>
              <li>Navigate to <strong>Settings</strong></li>
              <li>Scroll to the <strong>Danger Zone</strong> section at the bottom</li>
              <li>Click <strong>"Delete Account"</strong></li>
              <li>Type <strong>DELETE</strong> in the confirmation dialog and click <strong>"Delete My Account"</strong></li>
            </ol>
            <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <strong>What gets deleted:</strong> Your account profile, all connected platform tokens,
                synced analytics data, content items, uploaded media, deal/brand partnership records,
                revenue data, and notification history. This action is <strong>permanent and irreversible</strong>.
                Billing records are retained for 7 years as required by financial regulations.
              </p>
            </div>
          </section>

          {/* Method 3 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              3. Request Data Deletion via Email
            </h2>
            <p className="mb-4">
              If you prefer, or if you are unable to access your account, you can request data deletion
              by contacting our Privacy Team:
            </p>
            <div className="bg-gray-50 dark:bg-dark-800 rounded-xl p-6 border border-gray-200 dark:border-dark-700">
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Email</p>
                  <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    info@omniconnx.com
                  </a>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Subject Line</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Data Deletion Request - [Your Email Address]</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Include</p>
                  <ul className="list-disc list-outside ml-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>The email address associated with your OmniConnX account</li>
                    <li>Which data you want deleted (specific platform data, or all data)</li>
                    <li>Whether you want to keep your account active (platform data only) or delete everything</li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              We will verify your identity and process your request within <strong className="text-gray-900 dark:text-white">30 calendar days</strong>,
              as required by GDPR and CCPA.
            </p>
          </section>

          {/* Revoking access from platform side */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              4. Revoke Access from the Platform Side
            </h2>
            <p className="mb-4">
              You can also revoke OmniConnX's access directly from each social media platform:
            </p>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="mt-1 w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Facebook:</span>{' '}
                  Settings &amp; Privacy &rarr; Settings &rarr; Apps and Websites &rarr; Find OmniConnX &rarr; Remove
                </div>
              </div>
              <div className="flex gap-3">
                <div className="mt-1 w-2 h-2 rounded-full bg-pink-600 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Instagram:</span>{' '}
                  Settings &rarr; Security &rarr; Apps and Websites &rarr; Find OmniConnX &rarr; Remove
                </div>
              </div>
              <div className="flex gap-3">
                <div className="mt-1 w-2 h-2 rounded-full bg-red-600 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Google / YouTube:</span>{' '}
                  Visit{' '}
                  <a
                    href="https://myaccount.google.com/permissions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 dark:text-violet-400 hover:underline"
                  >
                    Google Account Permissions
                  </a>{' '}
                  &rarr; Find OmniConnX &rarr; Remove Access
                </div>
              </div>
              <div className="flex gap-3">
                <div className="mt-1 w-2 h-2 rounded-full bg-gray-900 dark:bg-white flex-shrink-0" />
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">TikTok:</span>{' '}
                  Settings and Privacy &rarr; Security &rarr; Manage app permissions &rarr; Find OmniConnX &rarr; Remove
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              Revoking access from the platform side will invalidate the OAuth tokens OmniConnX holds,
              preventing any further data access. To also delete data already stored in OmniConnX,
              please disconnect within OmniConnX or contact us via email.
            </p>
          </section>

          {/* What data we store */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              5. What Data We Store
            </h2>
            <p className="mb-4">
              For transparency, here is a summary of the data OmniConnX stores when you connect a platform:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
                <thead className="bg-gray-50 dark:bg-dark-800">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Data Type</th>
                    <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Deleted on Disconnect</th>
                    <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Deleted on Account Deletion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                  {[
                    ['OAuth access & refresh tokens', 'Yes', 'Yes'],
                    ['Platform username & ID', 'Yes', 'Yes'],
                    ['Synced analytics & metrics', 'On request', 'Yes'],
                    ['Uploaded content & media files', 'No (separate action)', 'Yes'],
                    ['Deal & brand partnership data', 'N/A', 'Yes'],
                    ['Account profile & settings', 'N/A', 'Yes'],
                    ['Billing & invoice records', 'N/A', 'Retained 7 years (legal)'],
                  ].map(([type, onDisconnect, onDelete]) => (
                    <tr key={type} className="bg-white dark:bg-dark-900">
                      <td className="p-3 font-medium text-gray-900 dark:text-white">{type}</td>
                      <td className="p-3">{onDisconnect}</td>
                      <td className="p-3">{onDelete}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Questions?
            </h2>
            <p>
              If you have any questions about data deletion or your privacy rights, please contact us at{' '}
              <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                info@omniconnx.com
              </a>{' '}
              or review our full{' '}
              <Link to="/privacy" className="text-violet-600 dark:text-violet-400 hover:underline">
                Privacy Policy
              </Link>.
            </p>
          </section>

          {/* Footer Links */}
          <div className="pt-4 pb-12 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>Related:</span>
            <Link to="/privacy" className="text-violet-600 dark:text-violet-400 hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-violet-600 dark:text-violet-400 hover:underline">Terms of Service</Link>
            <Link to="/cookies" className="text-violet-600 dark:text-violet-400 hover:underline">Cookie Policy</Link>
            <Link to="/contact" className="text-violet-600 dark:text-violet-400 hover:underline">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletionPage;
